import { z } from 'zod';
import { AllPaymentProvidersEnum } from '../../common-tools/providers';
/**
 * ClientContextV4Schema
 *
 * Optional metadata about the POS client making a v4 request. Stored for
 * audit/diagnostics only — never used for routing decisions.
 */
export const ClientContextV4Schema = z
    .object({
    deviceId: z.string().max(128).optional(),
    deviceName: z.string().max(128).optional(),
    appVersion: z.string().max(64).optional(),
    platform: z.enum(['IOS', 'ANDROID', 'WEB', 'DESKTOP']).optional(),
    posSessionId: z.string().max(128).optional(),
})
    .strict();
/** Client-generated idempotency key. Burned forever once used. */
export const IdempotencyKeyV4Schema = z.string().min(8).max(128);
/** Integer cents, zero allowed. All v4 money math is integer cents. */
export const MoneyCentsSchema = z.number().int().min(0);
/** Integer cents, strictly positive. */
export const PositiveMoneyCentsSchema = z.number().int().min(1);
/**
 * StripeLocalExecutionSchema
 *
 * Payload the frontend needs to execute a Stripe LOCAL_NETWORK payment
 * (Bluetooth reader / Tap to Pay / local Terminal SDK). The PaymentIntent is
 * created with automatic capture: money settles when the SDK confirms, so
 * the frontend treats PI `succeeded` as local success and reports it via
 * POST /v4/payment/local-update — the backend verifies with Stripe before
 * finalizing (never on client word alone).
 */
export const StripeLocalExecutionSchema = z.object({
    provider: z.literal(AllPaymentProvidersEnum.STRIPE),
    transactionId: z.string().uuid(),
    paymentIntentId: z.string(),
    paymentIntentClientSecret: z.string(),
    captureMethod: z.literal('automatic'),
    connectionToken: z.string(),
    connectionTokenEndpoint: z.string(),
    /** The business's connected Stripe account (the charge destination). */
    providerAccountId: z.string(),
    /**
     * Stripe Terminal location for reader discovery: the terminal's own
     * location, falling back to the platform default when the terminal has
     * none. Nullable only for schema tolerance — the backend always sends a
     * value.
     */
    locationId: z.string().nullable(),
    amountCents: MoneyCentsSchema,
    currency: z.string(),
    expiresAt: z.string(),
});
/**
 * BlockChypLocalExecutionSchema
 *
 * Payload the frontend needs to execute a BlockChyp LOCAL_NETWORK payment by
 * POSTing directly to the terminal on the LAN through the embedded BlockChyp
 * SDK client. transientCredentials are terminal-scoped and short-lived; the
 * requestTemplate must be sent verbatim — transactionRef is the canonical
 * transactionId and is what makes the attempt reconcilable server-side.
 */
const FutureIso8601TimestampSchema = z
    .string()
    .datetime()
    .refine((value) => Date.parse(value) > Date.now(), {
    message: 'Timestamp must be in the future',
});
const BlockChypLocalExecutionBaseSchema = z.object({
    provider: z.literal(AllPaymentProvidersEnum.BLOCKCHYP),
    transactionId: z.string().uuid(),
    transactionRef: z.string().uuid(),
    transientCredentials: z.object({
        apiKey: z.string().min(1),
        bearerToken: z.string().min(1),
        signingKey: z.string().min(1),
    }),
    requestTemplate: z.object({
        terminalName: z.string().min(1),
        amount: z.string(),
        transactionRef: z.string().uuid(),
        test: z.boolean(),
        promptForTip: z.boolean(),
        description: z.string(),
        timeout: z.number().int(),
    }),
    expiresAt: FutureIso8601TimestampSchema,
});
const BlockChypTerminalBaseSchema = z.object({
    terminalName: z.string().min(1),
    ipAddress: z.string().ip({ version: 'v4' }),
    port: z.literal(8443),
    protocol: z.literal('https'),
    chargePath: z.literal('/api/charge'),
});
/**
 * LOCAL_NETWORK is a PointCraft transport decision, not a reflection of the
 * provider's cloud-relay capability. The selected route is always LAN. The
 * provider flag is diagnostic metadata only and can never change
 * dispatch behavior.
 */
export const BlockChypLocalExecutionSchema = BlockChypLocalExecutionBaseSchema.extend({
    selectedTransport: z.literal('LAN'),
    providerCloudRelayEnabled: z.boolean(),
    routeCacheScope: z.string().regex(/^[a-f0-9]{64}$/),
    routeExpiresAt: FutureIso8601TimestampSchema,
    terminal: BlockChypTerminalBaseSchema,
});
export const LocalExecutionPayloadSchema = z.discriminatedUnion('provider', [
    StripeLocalExecutionSchema,
    BlockChypLocalExecutionSchema,
]);
/**
 * StripeOnlineExecutionSchema
 *
 * Payload for the online (terminal-less) branch. Two client paths,
 * discriminated by `paymentMethodType`:
 *
 * - `'card'` (MANUAL_CARD / WEB_PAYMENT / PAYMENT_LINK): true card-not-
 *   present — confirm `paymentIntentClientSecret` with the plain Stripe SDK
 *   (initialized with `publishableKey`).
 * - `'card_present'` (MOBILE_APP_PAYMENT — Tap to Pay): the merchant's
 *   phone is the reader. Initialize the Stripe Terminal SDK with
 *   `connectionToken` (refresh via `connectionTokenEndpoint`), register the
 *   local mobile reader at `locationId`, then collect + confirm the intent.
 *
 * Either way the intent is automatic-capture; the client then polls
 * POST /v4/sale/:saleId/payment-status for finalization. The client secret
 * and connection token are returned once per request and never persisted
 * server-side; idempotent replays re-mint them from the same PaymentIntent.
 */
export const StripeOnlineExecutionSchema = z.object({
    provider: z.literal(AllPaymentProvidersEnum.STRIPE),
    transactionId: z.string().uuid(),
    paymentIntentId: z.string(),
    paymentIntentClientSecret: z.string(),
    /** Platform publishable key — v4 intents are destination charges created
     *  on the platform account. */
    publishableKey: z.string(),
    /** 'card_present' = Tap to Pay via the Terminal SDK; 'card' = plain SDK. */
    paymentMethodType: z.enum(['card', 'card_present']),
    /** Tap to Pay (card_present) only — Terminal SDK credentials. */
    connectionToken: z.string().optional(),
    connectionTokenEndpoint: z.string().optional(),
    /** Tap to Pay only: location to register the phone reader under (the
     *  platform default — online payments have no terminal row). */
    locationId: z.string().optional(),
    /** Tap to Pay only: the business's connected Stripe account. */
    providerAccountId: z.string().optional(),
    amountCents: MoneyCentsSchema,
    currency: z.string(),
    captureMethod: z.literal('automatic'),
    expiresAt: z.string(),
});
export const OnlineExecutionPayloadSchema = z.discriminatedUnion('provider', [
    StripeOnlineExecutionSchema,
]);
/**
 * Cloud execution payloads: informational — the backend already drove the
 * terminal; the frontend polls POST /v4/sale/:saleId/payment-status.
 */
export const StripeCloudExecutionSchema = z.object({
    provider: z.literal(AllPaymentProvidersEnum.STRIPE),
    paymentIntentId: z.string(),
    readerId: z.string(),
    readerAction: z.string(),
});
export const BlockChypCloudExecutionSchema = z.object({
    provider: z.literal(AllPaymentProvidersEnum.BLOCKCHYP),
    transactionRef: z.string().uuid(),
    providerTerminalId: z.string(),
});
export const CloudExecutionPayloadSchema = z.discriminatedUnion('provider', [
    StripeCloudExecutionSchema,
    BlockChypCloudExecutionSchema,
]);
